import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    inject,
    Injector,
    NgZone,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { Section } from './layout/types/section.type';
import { SbSectionComponent } from './layout/builder/section/sb-section.component';
import { LayoutService } from './layout/services/layout.service';
import { combineLatest, combineLatestWith, filter, finalize, Subscription, take, tap } from 'rxjs';
import {
    AsyncPipe,
    isPlatformBrowser,
    isPlatformServer,
    registerLocaleData,
    ViewportScroller,
} from '@angular/common';
import { accounts } from 'google-one-tap';
import { environment } from '../environments/environment';
import { AuthService } from './common/services/auth.service';
import { ToastsContainer } from './common/components/toast/toasts-container.component';
import { ServerTokenService } from './common/services/server-token.service';
import { UserSessionService } from './common/services/user-session.service';
import { ToastService, ToastType } from './common/components/toast/toast.service';
import { PageBuilderService } from './layout/services/page-builder.service';
import { PageSettings } from './layout/types/page-settings.type';
import { LayoutBuilderService } from './layout/services/layout-builder.service';
import localeEn from '@angular/common/locales/en';
import { REQUEST, RESPONSE } from '../express.token';
import { NgxLoadingBar } from '@ngx-loading-bar/core';
import { ApplicationRef } from '@angular/core';
import { ConnectionState } from 'ng-connection-service';
import { NetworkBannerComponent } from './components/network-banner/network-banner.component';
import { PreviewBannerComponent } from './components/preview-banner/preview-banner.component';
import { GlobalService } from './common/services/global.service';
import { ThemeBuilderService } from './common/services/theme-builder.service';
import { Theme } from './common/types/global-settings';
import { PAGE_VIEW_RESTRICTED_URL } from './common/data/page-view.data';
import { PageViewDataService } from './common/services/page-view.service';
import { Title } from '@angular/platform-browser';
import { ForcedRegistrationService } from './common/services/forced-registration.service';
import { AgentReferralService } from './common/services/agent-referral.service';
import { PhoneNumberPromptGoogleSSO } from './common/services/phone-number-prompt.service';
import { CustomSiteSnippet } from './common/types/custome-snippet.type';
import { SnippetInjectorService } from './common/services/snippet-injector.service';

registerLocaleData(localeEn);
declare const google: any;
@Component({
    selector: 'app-root',
    standalone: true,
    imports: [
        RouterOutlet,
        SbSectionComponent,
        ToastsContainer,
        NgxLoadingBar,
        NetworkBannerComponent,
        PreviewBannerComponent,
        AsyncPipe,
    ],
    providers: [ServerTokenService],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
    private noScrollRoutes = ['/home'];
    private layoutService = inject(LayoutService);
    private router = inject(Router);
    private viewportScroller = inject(ViewportScroller);
    private authService = inject(AuthService);
    private layoutBuilderService = inject(LayoutBuilderService);
    private pageBuilderService = inject(PageBuilderService);
    private pageViewService = inject(PageViewDataService);
    private titleService = inject(Title);
    
    /**
     * Page Settings
     */
    pageSettings?: PageSettings;

    /**
     * page query check flag
     */
    isPageQueryChecked?: boolean = false;

    /**
     * Sections
     */
    headerAreaSections: Section[] = [];
    footerAreaSections: Section[] = [];

    /**
     * Checking is maintenance mode or not
     */
    isUnderMaintenance: boolean = false;

    /**
     * Taking the company name
     */
    companyName?: string;

    /**
     * Company logo
     */
    logoUrl?: string;

    status!: string;

    /**
     * Flag to track the network availability
     */
    currentState!: ConnectionState;

    /**
     * To save config values for maintenance mode
     */
    maintenanceConfig: { [key: string]: string } = {}

    /**
     * To check whether the hydration is over
     */
    isHydrationOver: boolean = false;

    /**
     * Subscriptions of this component
     */
    private subscriptions = {
        init: null as Subscription | null,
        connectivity: null as Subscription | null,
        maintenanceConfig: null as Subscription | null,
        googleSSOClientId: null as Subscription | null,
        customSiteSnippets: null as Subscription | null,
        companyDetails: null as Subscription | null,
        closedProperties: null as Subscription | null,
    };

    private appRef = inject(ApplicationRef);
    private cd = inject(ChangeDetectorRef);
    private userSessionService = inject(UserSessionService);
    private platformId = inject(PLATFORM_ID);
    private toastService = inject(ToastService);
    private req = inject(REQUEST, { optional: true });
    private injector = inject(Injector);
    private res = inject(RESPONSE, { optional: true });
    private themeBuilderService = inject(ThemeBuilderService);
    public globalService = inject(GlobalService);
    private ngZone = inject(NgZone);
    private forcedRegistrationService = inject(ForcedRegistrationService);
    private phoneNumberPromptService = inject(PhoneNumberPromptGoogleSSO);
    private snippetInjectorService = inject(SnippetInjectorService);
    private agentReferralService = inject(AgentReferralService);

    public isBrowser = isPlatformBrowser(this.platformId);

    async ngOnInit(): Promise<void> {

        // Initialise closedPropertiesEnabled early so canMatch on browse/homes routes
        // resolves before Angular's route matching phase completes.
        this.subscriptions['closedProperties'] = this.layoutService.getClosedPropertiesStatus().pipe(take(1)).subscribe({
            next: (response) => {
                this.globalService.closedPropertiesEnabled.next(
                    response.body?.data?.closed_properties_enabled ?? false
                );
            },
            error: () => this.globalService.closedPropertiesEnabled.next(false),
        });

        this.router.events.pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(()=>{
            this.phoneNumberPromptService.checkRecurringTime();
        })

        this.globalService.theme.subscribe(value => {
            if (!value) return;
            this.applyTheme(value);
        })

        this.appRef.whenStable().then((value) => {
            this.isHydrationOver = true;
        });

        this.forcedRegistrationService.assignUserTypeAndPropertyViewCount()

        // Capture the referring-agent slug from `?ref=<slug>` on the inbound URL so a lead who
        // registers or submits a form later gets attributed to that agent (see AgentReferralService
        // and the auth/forms services that append it to their payloads).
        this.agentReferralService.capture();

        // Redirect to the root page if the 'page' query parameter is invalid
        if (!this.isBrowser && this.req && this.res) {
            const routeQueryParams = this.req.query;

            if (routeQueryParams && Object.keys(routeQueryParams).includes('page')) {
                const page = Number(routeQueryParams['page']);
                const formattedPage = isNaN(page) || page < 1 ? 1 : page;

                if (formattedPage !== page) {
                    // Construct the URL without the invalid 'page' parameter
                    const url = new URL(this.req.url, `http://${this.req.headers.host}`);
                    url.searchParams.delete('page');

                    return this.res.redirect(301, url.pathname + url.search);
                }
            }
        }
        this.isPageQueryChecked = true;
        combineLatest({
            pageSettings: this.pageBuilderService.getSettings,
            headerAreaSections: this.layoutBuilderService.headerAreaSections,
            footerAreaSections: this.layoutBuilderService.footerAreaSections,
        }).subscribe({
            next: (value) => {
                // Ensure pageSettings object is not empty
                if (value.footerAreaSections.length && value.headerAreaSections.length) {
                    this.headerAreaSections = value.headerAreaSections;
                    this.footerAreaSections = value.footerAreaSections;
                    // Manually trigger change detection after async update
                    this.cd.detectChanges();
                }

                if (Object.keys(value.pageSettings).length) {
                    this.pageSettings = value.pageSettings;
                    // Manually trigger change detection after async update
                    this.cd.detectChanges();
                }
                
                // Scroll page to the top after data loaded on specific conditions
                if (this.pageSettings?.pageLayoutType !== 'listing-page' && this.isHydrationOver) {
                    this.viewportScroller.scrollToPosition([0, 0]);
                }
            },
            complete: () => {},
        });

        // to show banner when internet connectivity is lost
        if (this.isBrowser) {
            this.status = 'ONLINE';
            //dynamically import ConnectionService to avoid errors during SSR
            const { ConnectionService } = await import('ng-connection-service');

            //Dynamically inject service to avoid angular errors
            const connectionService = this.injector.get(ConnectionService);
            this.subscriptions['connectivity'] = connectionService
                .monitor()
                .pipe(
                    tap((newState: ConnectionState) => {
                        this.currentState = newState;
                        if (this.currentState.hasNetworkConnection) {
                            this.status = 'ONLINE';
                            this.cd.detectChanges();
                        } else {
                            this.status = 'OFFLINE';
                            this.cd.detectChanges();
                        }
                    }),
                )
                .subscribe();
        }

        this.globalService.companyDetails
            .pipe(
                filter(Boolean),
                take(1)
            )
            .subscribe((company) => {
                if (isPlatformServer(this.platformId) && this.res) {
                    this.res.locals['company'] = {
                        ...(this.res.locals['company'] || {}),
                        name: company.name,
                        domain: `https://${company.domain}`,
                    };
                }
            });

        this.globalService.branding
            .pipe(
                filter(Boolean),
                take(1)
            )
            .subscribe((branding) => {
                if (isPlatformServer(this.platformId) && this.res) {                    
                    this.res.locals['company'] = {
                        ...(this.res.locals['company'] || {}),
                        logo: branding.company_logo
                    };
                }
            });

        this.globalService.socialLinks
        .pipe(
            filter(links => Array.isArray(links) && links.length > 0),
            take(1)
        )
        .subscribe((links) => {
            if (isPlatformServer(this.platformId) && this.res) {
                this.res.locals['social_links'] = links;
            }
        });

        // Scroll to top on route change
        // this.router.events
        //     .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        //     .subscribe((event: NavigationEnd) => {
        //         if (!this.noScrollRoutes.includes(event.urlAfterRedirects)) {
        //             this.viewportScroller.scrollToPosition([0, 0]);
        //         }
        //     });
        this.subscriptions['googleSSOClientId'] = this.globalService.googleSSOSettings.subscribe((params) => {
            if (isPlatformBrowser(this.platformId)) {
                this.loadGoogleSSOScript().then(() => {
                    if (this.userSessionService.getAccessToken()) {
                        return;
                    }

                    const isPaidTraffic: boolean = this.forcedRegistrationService.getUserType() === 'paid_traffic';

                    // Suppress One Tap on blog pages unless an admin has enabled it there (AGE-FGCTYT).
                    // `googleSSOSettings` is emitted by the layout resolver, which runs BEFORE the router
                    // finishes navigating — so `this.router.url` is still '/' here. Use the real browser
                    // URL instead, which already reflects the page the user landed on (e.g. a blog post link).
                    const currentPath: string = window.location?.pathname ?? this.router.url;
                    const isBlogPage: boolean = currentPath.startsWith('/blog');
                    const blockedOnBlog: boolean = isBlogPage && !params?.blog_one_tap_status;

                    if (
                        params?.status &&
                        params?.client_id &&
                        params?.google_one_tap_status &&
                        !isPaidTraffic &&
                        !blockedOnBlog
                    ) {
                        this.showGoogleOneTapPrompt();
                    }
                });
            }
        });

        this.subscriptions['customSiteSnippets'] = this.globalService.customSiteSnippets
            .pipe(filter(Boolean), take(1))
            .subscribe((data: any) => {
                    this.snippetInjectorService.inject(data)
            });

        // this.subscriptions['companyDetails'] = this.globalService.companyDetails
        //     .subscribe((data: any) => {
        //         if (data?.status === 'active' && data?.is_ppc_enabled) {
        //             this.snippetInjectorService.injectPPCSnippets();
        //         }
        //     });

        /**
         * Taking the maintenance mode config and checking is active or not.
         */
        // Only for server context
        this.subscriptions['maintenanceConfig'] = this.globalService.maintenanceConfig.subscribe((data) => {
            if (data.isMaintenance) {
                this.isUnderMaintenance = true;
                this.companyName = data.company?.name;
                this.logoUrl = data.company?.logo_url;
                this.maintenanceConfig = {
                    messageTitle: data.messageTitle || "We'll Be Back Soon!",
                    messageContent: data.messageContent || `${this.companyName || 'Site'} is down for maintenance. Please check back in a few hours.`,
                }
                if (isPlatformServer(this.platformId) && this.res && !this.res.headersSent) {
                    this.res.status(503);
                    this.res.header('Retry-After', data.retryAfter || '10800');
                }
                return;
            } 
            this.isUnderMaintenance = false;
        });

        //Reset the isNotfound flag
        this.router.events.pipe(
            filter((event): event is NavigationStart => event instanceof NavigationStart),
        ).subscribe((event) => {
            this.globalService.isNotFoundPage.next(true);
            // The blog post resolver turns it back on for a previewed post
            this.globalService.blogPreview.next(false);
        });        

        //Page view handler
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        ).subscribe((event) => {
            if (isPlatformBrowser(this.platformId)) {
                const fullUrl = window.location.origin + event.urlAfterRedirects;
                this.handlePageView(fullUrl);
            }
        }); 
        // Check for tokens
        this.userSessionService.checkForTokens();

        const queryParams = this.req?.query;
        const personId = queryParams?.['person_id'];
        const signature = queryParams?.['signature'];
        let userId = queryParams?.['user_id'];
        const isEmail = queryParams?.['is_email'];
        if ((personId && signature) || (userId && isEmail === 'yes')) {
            if (!isPlatformServer(this.platformId)) {
                return;
            }

            if (userId && !Number.isInteger(+userId)) {
                userId = atob(userId as string);
            }
            AuthService.loginProgress.next(true);
            const postData =
                personId && signature
                    ? { type: 'signature', person_id: personId, signature }
                    : { type: 'signature', person_id: userId };
            this.authService
                .login(postData)
                .pipe(finalize(() => AuthService.loginProgress.next(false)))
                .subscribe({
                    next: (response) => {
                        this.userSessionService.createUserSession(
                            response.body?.data?.access_token,
                            response.body?.data?.refresh_token,
                            response.body?.data?.user,
                        );
                    },
                    error: (err) => {},
                });
            return;
        }

        if (this.userSessionService.getAccessToken()) {
            this.authService.init().subscribe({
                next: (response) => {
                    this.userSessionService.authData.next(response.body?.data.user);
                },
                error: (error) => {
                    // Handle Error
                },
            });
            return;
        }
    }

    applyTheme(theme: Theme) {
        const themeColors = theme.colors;
        this.themeBuilderService.build(themeColors);
        // Object.keys(this.themeBuilderService.colors).forEach((key) => {
        //     this.elementRef.nativeElement.
        // })
    }

    /**
     * Checking is restricted url 
     * @param currentUrl 
     * @returns 
     */
    isRestrictedUrl(currentUrl: string): boolean {
        return PAGE_VIEW_RESTRICTED_URL.some(path => currentUrl.includes(`/${path}`));
    }

    /**
     * Handling the page views api calling
     * @param currentUrl 
     */
    private handlePageView(currentUrl: string) {
        if (this.isRestrictedUrl(currentUrl)) {
            return;
        }
        
        if (this.globalService.isNotFoundPage.value) {
            this.pageViewService.postPageViewDetails({
                page_url: currentUrl,
                page_title: this.titleService.getTitle(),
            }).subscribe({
                next: (res) => {
                    if (res.body?.data?.page_view?.person_id === null) {
                        this.pageViewService.appendId(res.body?.data?.page_view?.id);
                    } else {
                        this.pageViewService.clear();
                    }
                },
                error: (err) => {}
            });
        }
    }

    /**
     * To load google SSO script
     * @returns
     */
    private loadGoogleSSOScript(): Promise<void> {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.defer = true;
            script.async = true;
            script.onload = () => resolve();
            document.body.appendChild(script);
        });
    }

    /**
     * To show Google One Tap prompt
     */
    private showGoogleOneTapPrompt() {
        this.ngZone.runOutsideAngular(() => {
            const gAccounts: accounts = google.accounts;
            gAccounts.id.initialize({
                client_id: this.globalService.googleSSOSettings.value?.client_id!,
                auto_select: true,
                callback: ({ credential }) => {
                    this.ngZone.run(() => {
                        const postData: {
                            type: string;
                            token_id: string;
                            page_view_ids?: number[];
                        } = { type: 'google', token_id: credential };

                        // One tap creates a brand new lead just like the sign-in buttons in the
                        // login and forced registration modals, so it has to hand over the pages
                        // collected while the visitor was anonymous — the backend only backfills
                        // them onto the lead when page_view_ids is non-empty.
                        const pageViewIds = this.pageViewService.getIds();
                        if (pageViewIds.length > 0) {
                            postData.page_view_ids = pageViewIds;
                        }

                        this.authService.login(postData)
                        .subscribe({
                            next: (response) => {
                            this.pageViewService.clear();
                            this.userSessionService.createUserSession(
                                response.body?.data?.access_token,
                                response.body?.data?.refresh_token,
                                response.body?.data?.user,
                            );
                            },
                            error: (err) => {
                                this.toastService.show(err.error, ToastType.ERROR);
                            },
                        });
                    });
                },
            });
            google.accounts.id.prompt();
        });
    }

    ngOnDestroy(): void {
        Object.values(this.subscriptions).forEach((subscription) => {
            subscription?.unsubscribe();
        });
    }
}
